import { getUnprocessableErrors } from '@10xlabs/utils'
import { ApolloError } from '@apollo/client'
import { Formik, getIn, useFormikContext } from 'formik'
import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { newBusinessClientUserSchema } from 'features/BusinessClients/components/NewBusinessClientUser/schema'
import { INewBusinessClientUserFormValues } from 'features/BusinessClients/types'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { BusinessClientSuggestion, BusinessClientUser } from 'gql/generated/graphql'
import { Checkbox } from 'components/v2/Checkbox'
import styled from 'styled-components'
import { Label } from 'components/v2/Label'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 60%;
`

const messages = defineMessages({
  isAdminRolePlaceholder: {
    id: 'businessClients.users.form.isAdminRole.placeholder',
    defaultMessage: 'Has Admin Role'
  },
  businessSitePlaceholder: {
    id: 'businessClients.users.form.businessSite.placeholder',
    defaultMessage: 'Select business site'
  },
  firstNamePlaceholder: {
    id: 'businessClients.users.form.firstName.placeholder',
    defaultMessage: 'Enter first name'
  },
  lastNamePlaceholder: {
    id: 'businessClients.users.form.lastName.placeholder',
    defaultMessage: 'Enter last name'
  },
  phonePlaceholder: {
    id: 'businessClients.users.form.phone.placeholder',
    defaultMessage: 'Enter phone'
  },
  emailPlaceholder: {
    id: 'businessClients.users.form.email.placeholder',
    defaultMessage: 'Enter email'
  },
  titlePlaceholder: {
    id: 'businessClients.users.form.title.placeholder',
    defaultMessage: 'Enter title'
  }
})

interface INewBusinessClientUserFormProps {
  businessClientId: string
  loading?: boolean
  isEdit?: boolean
  error?: ApolloError
}

const NewBusinessClientUserForm = ({
  businessClientId,
  loading,
  error,
  isEdit
}: INewBusinessClientUserFormProps): ReactElement => {
  const intl = useIntl()
  const { values, setValues, setFieldValue, handleChange, handleBlur, touched, errors, dirty } =
    useFormikContext<INewBusinessClientUserFormValues>()

  return (
    <>
      <FormLayout.Card>
        <FormLayout.CardHeader>
          <Heading.H4>
            <FormattedMessage id="businessClients.user.form.title" defaultMessage="User information" />
          </Heading.H4>
        </FormLayout.CardHeader>
        <Row noMargin>
          <Column width="50%">
            <BusinessClientAutocomplete
              id="businessClient"
              value={values.businessClient?.name || ''}
              onChange={(businessClient): void => {
                setValues({
                  ...values,
                  businessClient: businessClient?.id
                    ? {
                        id: businessClient.id,
                        name: businessClient.name
                      }
                    : null,
                  businessSite: null
                })
              }}
              onBlur={handleBlur}
              placeholder={intl.formatMessage({
                id: 'businessClients.users.form.businessClient.placeholder',
                defaultMessage: 'Select business client'
              })}
              label={
                <FormattedMessage
                  id="businessClients.users.form.businessClient.label"
                  defaultMessage="Business Client"
                />
              }
              touched={getIn(touched, 'businessClient')}
              errors={getIn(errors, 'businessClient')}
              disabled={!isEdit}
            />
          </Column>
          <Column width="50%">
            <BusinessSiteAutocomplete
              id="businessSite"
              businessClientId={values.businessClient?.id}
              value={values.businessSite?.name || ''}
              onChange={(businessSite): void => {
                setFieldValue(
                  'businessSite',
                  businessSite?.id
                    ? {
                        id: businessSite.id,
                        name: businessSite.name
                      }
                    : null
                )
              }}
              onBlur={handleBlur}
              placeholder={intl.formatMessage(messages.businessSitePlaceholder)}
              label={
                <FormattedMessage id="businessClients.users.form.businessSite.label" defaultMessage="Business Site" />
              }
            />
          </Column>
        </Row>
        <Row noMargin>
          <Column>
            <Input
              id="firstName"
              value={values.firstName}
              label={<FormattedMessage id="businessClients.users.form.firstName.label" defaultMessage="First Name" />}
              placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.firstName}
              errors={errors.firstName}
            />
          </Column>
          <Column>
            <Input
              id="lastName"
              value={values.lastName}
              label={<FormattedMessage id="businessClients.users.form.lastName.label" defaultMessage="Last Name" />}
              placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.lastName}
              errors={errors.lastName}
            />
          </Column>
        </Row>
        <Row noMargin>
          <Column>
            <Input
              id="phone"
              value={values.phone}
              label={<FormattedMessage id="businessClients.users.form.phone.label" defaultMessage="Phone" />}
              placeholder={intl.formatMessage(messages.phonePlaceholder)}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.phone}
              errors={errors.phone}
            />
          </Column>
          <Column>
            <Input
              id="email"
              value={values.email}
              label={<FormattedMessage id="businessClients.users.form.email.label" defaultMessage="Email" />}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.email}
              errors={errors.email}
            />
          </Column>
        </Row>
        <Row noMargin>
          <Column width="50%">
            <Input
              id="title"
              value={values.title}
              label={<FormattedMessage id="businessClients.users.form.title.label" defaultMessage="Title" />}
              placeholder={intl.formatMessage(messages.titlePlaceholder)}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.title}
              errors={errors.title}
            />
          </Column>
          <Column width="50%">
            <Label>
              <FormattedMessage id="businessClients.users.form.hasAdminRole.label" defaultMessage="Admin Role" />
            </Label>
            <CheckboxContainer>
              <Checkbox
                id="isAdminRole"
                checked={values.hasAdminRole}
                onChange={(): void => {
                  setFieldValue('hasAdminRole', !values.hasAdminRole)
                }}
                onBlur={handleBlur}
              />
              <FormattedMessage id="businessClients.users.form.hasAdminRole.label" defaultMessage="Has Admin Role" />
            </CheckboxContainer>
          </Column>
        </Row>
      </FormLayout.Card>
      {error && (
        <FormLayout.Actions>
          <ErrorMessage errors={getUnprocessableErrors(error)}>
            <FormattedMessage
              id="businessClients.users.form.error"
              defaultMessage="There was an error while creating your user"
            />
          </ErrorMessage>
        </FormLayout.Actions>
      )}
      <FormLayout.Actions>
        <Button type="button" as={NavLink} to={`/business-clients/${businessClientId}/users`} kind="cancel">
          <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
          {isEdit ? (
            <FormattedMessage id="dictionary.update" defaultMessage="Update" />
          ) : (
            <FormattedMessage id="dictionary.save" defaultMessage="Save" />
          )}
        </Button>
      </FormLayout.Actions>
    </>
  )
}

interface INewBusinessClientUserProps {
  onSubmit: (values: INewBusinessClientUserFormValues) => void
  businessClientId: string
  loading?: boolean
  isEdit?: boolean
  error?: ApolloError
  businessClientUser?: BusinessClientUser
  businessClientAutocomplete?: BusinessClientSuggestion
}

export const NewBusinessClientUser = ({
  onSubmit,
  businessClientId,
  businessClientAutocomplete,
  businessClientUser,
  loading,
  error,
  isEdit
}: INewBusinessClientUserProps): ReactElement => {
  const intl = useIntl()

  const initialClientFormValues: INewBusinessClientUserFormValues = {
    businessClient: businessClientUser?.businessClient
      ? {
          id: businessClientUser.businessClient.id,
          name: businessClientUser.businessClient.name
        }
      : {
          id: businessClientId,
          name: businessClientAutocomplete?.name || ''
        },
    businessSite: businessClientUser?.businessSite
      ? {
          id: businessClientUser.businessSite.id,
          name: businessClientUser.businessSite.name
        }
      : null,
    firstName: businessClientUser?.userAccount.firstName || '',
    lastName: businessClientUser?.userAccount.lastName || '',
    phone: businessClientUser?.userAccount.phoneNumber?.number || '',
    email: businessClientUser?.userAccount.email || '',
    title: businessClientUser?.title || '',
    hasAdminRole: businessClientUser?.hasAdminRole || false
  }

  return (
    <Formik
      initialValues={initialClientFormValues}
      validationSchema={newBusinessClientUserSchema(intl)}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ handleSubmit }): ReactElement => {
        return (
          <FormLayout.Content as={'form'} onSubmit={handleSubmit}>
            <NewBusinessClientUserForm
              businessClientId={businessClientId}
              loading={loading}
              error={error}
              isEdit={isEdit}
            />
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
